<script setup>
import { computed, onBeforeUnmount, reactive, ref, watch } from 'vue'
import { Award, Sprout, Droplets, Layers, Flower2, Sun, Bird, Egg, Snowflake } from '@lucide/vue'
import { playFeedback } from '../feedback'

const props = defineProps({
  badges: { type: Array, default: () => [] },
})
const emit = defineEmits(['done'])

/* 队列：铜→银→金→传说 依次登场，高潮留最后 */
const rarityWeight = { copper: 0, silver: 1, gold: 2, legend: 3 }
const list = ref([])
const index = ref(0)
const current = computed(() => list.value[index.value] || null)
const isGrand = computed(() => !!current.value && ['gold', 'legend'].includes(current.value.rarity))

let timer = null
function armTimer() {
  window.clearTimeout(timer)
  if (!current.value) return
  if (isGrand.value) playFeedback('complete')
  timer = window.setTimeout(advance, isGrand.value ? 3000 : 2400)
}
function advance() {
  window.clearTimeout(timer)
  if (index.value + 1 < list.value.length) { index.value += 1; armTimer() }
  else emit('done')
}
watch(() => props.badges, v => {
  list.value = [...v].sort((a, b) => (rarityWeight[a.rarity] ?? 0) - (rarityWeight[b.rarity] ?? 0))
  index.value = 0
  if (list.value.length) armTimer()
}, { immediate: true })
onBeforeUnmount(() => window.clearTimeout(timer))

/* 图片素材约定同 BadgeAlbum：/badges/medallion-{rarity}.png，失败退化纯 CSS */
const brokenMedallions = reactive({})
const medallionSrc = rarity => `/badges/medallion-${rarity || 'copper'}.png`
const onMedallionError = rarity => { brokenMedallions[rarity] = true }

const iconByCode = {
  seed: Sprout, seeds: Sprout, dew: Droplets, streak: Droplets,
  soil: Layers, reviews: Layers, pollen: Flower2, pollination: Flower2, mastery: Flower2,
  gardener: Sun, habit: Sun, hummingbird: Bird, precision: Bird,
  hidden: Egg, easter: Egg, secret: Egg, seasonal: Snowflake, season: Snowflake,
}
const iconFor = badge => iconByCode[String(badge?.collection || '').toLowerCase()] || Award

/* 花瓣：金/传说全屏时飘落，色调取自花园色板（随机性只在表现层） */
const petalTints = ['var(--gold)', 'var(--terracotta)', 'var(--sage)', 'var(--sage-soft)']
const petals = Array.from({ length: 18 }, (_, i) => ({
  left: ((i * 53) % 97) + Math.random() * 3,
  delay: Math.random() * 1.1,
  dur: 2.4 + Math.random() * 1.8,
  size: 7 + Math.round(Math.random() * 8),
  sway: `${(Math.random() * 14 - 7).toFixed(1)}vw`,
  tint: petalTints[i % petalTints.length],
}))
</script>

<template>
  <Teleport to="body">
    <Transition name="badgepop" mode="out-in">
      <!-- 金 / 传说：全屏克制庆祝，3 秒自动退或点击跳过 -->
      <div v-if="current && isGrand" :key="current.code" class="cele-overlay" @click="advance">
        <span v-for="(p, i) in petals" :key="i" class="cele-petal" :style="{ left: p.left + '%', width: p.size + 'px', height: Math.round(p.size * 1.4) + 'px', background: p.tint, animationDelay: p.delay + 's', animationDuration: p.dur + 's', '--sway': p.sway }"></span>
        <div class="cele-medallion" :class="`rarity-${current.rarity}`">
          <img v-if="!brokenMedallions[current.rarity]" :src="medallionSrc(current.rarity)" alt="" @error="onMedallionError(current.rarity)" />
          <component :is="iconFor(current)" />
        </div>
        <div class="cele-card">
          <p class="eyebrow">NEUER ERFOLG · 新徽章</p>
          <h2>{{ current.name }}</h2>
          <span>{{ current.desc }}</span>
          <small class="cele-hint">{{ list.length > 1 ? `${index + 1} / ${list.length} · 点击继续` : '点击任意处关闭' }}</small>
        </div>
      </div>
      <!-- 铜 / 银：页内浮出卡片 -->
      <div v-else-if="current" :key="current.code" class="cele-toast" :class="`rarity-${current.rarity}`" @click="advance">
        <span class="cele-toast-medallion">
          <img v-if="!brokenMedallions[current.rarity]" :src="medallionSrc(current.rarity)" alt="" @error="onMedallionError(current.rarity)" />
          <component :is="iconFor(current)" />
        </span>
        <span class="cele-toast-info">
          <b>{{ current.name }}</b>
          <small>{{ current.desc }}</small>
        </span>
        <em v-if="list.length > 1" class="cele-toast-count">{{ index + 1 }}/{{ list.length }}</em>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
/* —— 全屏庆祝（金/传说）：幕布 + 花瓣 + 绽放，复用 garden-celebration 的动效思路 —— */
.cele-overlay { position: fixed; inset: 0; z-index: 140; display: grid; place-content: center; justify-items: center; text-align: center; padding: 24px; overflow: hidden; cursor: pointer; background: rgba(245, 240, 230, .88); -webkit-backdrop-filter: blur(12px); backdrop-filter: blur(12px); }
[data-theme="dark"] .cele-overlay { background: rgba(22, 27, 23, .86); }

.cele-petal { position: absolute; top: -4vh; border-radius: 62% 38% 55% 45%; opacity: 0; pointer-events: none; animation: badge-petal-fall linear forwards; }
@keyframes badge-petal-fall {
  0% { transform: translate3d(0, -6vh, 0) rotate(0deg); opacity: 0; }
  10% { opacity: .85; }
  100% { transform: translate3d(var(--sway, 4vw), 108vh, 0) rotate(340deg); opacity: .12; }
}

.cele-medallion { position: relative; width: 132px; height: 132px; border-radius: 50%; display: grid; place-items: center; color: var(--gold); background: color-mix(in srgb, var(--gold) 26%, transparent); box-shadow: 0 22px 60px var(--sage-glow); animation: badge-bloom .7s cubic-bezier(.2, .8, .2, 1); }
.cele-medallion.rarity-legend { box-shadow: 0 0 0 3px var(--sage-glow), 0 24px 70px var(--sage-glow); }
.cele-medallion img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: contain; }
.cele-medallion svg { position: relative; width: 56px; height: 56px; }

.cele-card { max-width: min(430px, 88vw); margin-top: -22px; padding: 20px 28px 18px; border-radius: 20px; background: var(--glass-bg-strong); -webkit-backdrop-filter: blur(var(--glass-blur-strong)) saturate(160%); backdrop-filter: blur(var(--glass-blur-strong)) saturate(160%); border: 1px solid var(--glass-border); box-shadow: var(--glass-highlight), var(--glass-shadow); animation: badge-lift .55s .12s both; }
.cele-card h2 { font: 700 24px/1.2 var(--font-display); margin: 4px 0 6px; }
.cele-card span { color: var(--muted); line-height: 1.55; }
.cele-hint { display: block; margin-top: 12px; color: var(--muted-2); font-size: 11px; }

/* —— 页内浮出卡片（铜/银） —— */
.cele-toast { position: fixed; z-index: 130; left: 50%; bottom: max(96px, calc(env(safe-area-inset-bottom) + 88px)); transform: translateX(-50%); display: flex; align-items: center; gap: 12px; width: min(430px, calc(100vw - 32px)); padding: 12px 14px; border-radius: var(--radius-lg); background: var(--glass-bg-strong); -webkit-backdrop-filter: blur(var(--glass-blur-strong)) saturate(160%); backdrop-filter: blur(var(--glass-blur-strong)) saturate(160%); border: 1px solid var(--glass-border); box-shadow: var(--glass-highlight), var(--glass-shadow-hover); cursor: pointer; }
.cele-toast-medallion { position: relative; flex: none; width: 46px; height: 46px; border-radius: 50%; display: grid; place-items: center; }
.cele-toast-medallion img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: contain; }
.cele-toast-medallion svg { position: relative; width: 22px; height: 22px; }
.rarity-copper .cele-toast-medallion { background: color-mix(in srgb, var(--terracotta) 22%, transparent); color: var(--terracotta); }
.rarity-silver .cele-toast-medallion { background: color-mix(in srgb, var(--muted) 20%, transparent); color: var(--muted); }
.rarity-gold .cele-toast-medallion, .rarity-legend .cele-toast-medallion { background: color-mix(in srgb, var(--gold) 24%, transparent); color: var(--gold); }
.cele-toast-info { display: grid; gap: 2px; min-width: 0; flex: 1; text-align: left; }
.cele-toast-info b { font-size: 14px; }
.cele-toast-info small { color: var(--muted); font-size: 12px; line-height: 1.4; }
.cele-toast-count { font-style: normal; color: var(--muted-2); font-size: 11px; flex: none; }

@keyframes badge-bloom { 0% { opacity: 0; transform: scale(.72) rotate(-3deg); } 70% { transform: scale(1.04) rotate(1deg); } 100% { opacity: 1; transform: scale(1); } }
@keyframes badge-lift { 0% { opacity: 0; transform: translateY(8px); } 100% { opacity: 1; transform: translateY(0); } }

.badgepop-enter-active, .badgepop-leave-active { transition: opacity .3s ease, transform .3s ease; }
.badgepop-enter-from, .badgepop-leave-to { opacity: 0; }
.badgepop-enter-from.cele-toast, .badgepop-leave-to.cele-toast { transform: translateX(-50%) translateY(14px); }

@media (prefers-reduced-motion: reduce) {
  .cele-petal { display: none; }
  .cele-medallion, .cele-card { animation: none; }
  .badgepop-enter-active, .badgepop-leave-active { transition: none; }
}
@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
  .cele-card, .cele-toast { background: var(--glass-fallback); -webkit-backdrop-filter: none; backdrop-filter: none; }
}
</style>
